import React from 'react';
import { BookOpen, Clock, Film, ArrowRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { Badge } from '../ui/badge';

/**
 * LinkedInExportGuide
 * Inline accordion sitting just above the PDF dropzone on the Dashboard.
 * Walks a first-time user through the LinkedIn "Save to PDF" export in 4 steps.
 *
 * A short looped GIF slot is reserved at the top of the accordion body. Drop
 * the actual asset URL into `GIF_URL` (or leave null for the styled
 * "Animation coming soon" placeholder).
 */

// TODO: paste the recorded GIF URL here (5-7 sec, ~600px wide). Leave as null
// to keep the placeholder slot visible.
const GIF_URL = 'https://customer-assets.emergentagent.com/job_profile-edge-ai/artifacts/2qqf4po4_LinkedIn-PDF-Profile.gif';

const STEPS = [
  {
    n: 1,
    title: 'Open your LinkedIn profile',
    detail:
      'Go to linkedin.com and click your face/avatar (top right) \u2192 "View Profile".',
  },
  {
    n: 2,
    title: 'Click the "Resources" button',
    detail:
      'Right under your name and headline, you\u2019ll see Open to / Add section / Book an appointment / Resources. Click "Resources".',
  },
  {
    n: 3,
    title: 'Choose "Save to PDF"',
    detail:
      'From the dropdown pick "Save to PDF". LinkedIn generates a print-ready PDF of your full profile in a few seconds and downloads it automatically.',
  },
  {
    n: 4,
    title: 'Drop the PDF into the box below',
    detail:
      'Drag-and-drop or click "Choose PDF". We\u2019ll auto-extract your headline, About, experiences, skills, and education.',
  },
];

export function LinkedInExportGuide() {
  return (
    <Accordion
      type="single"
      collapsible
      className="mb-4 group/guide"
      data-testid="linkedin-export-guide"
    >
      <AccordionItem
        value="how-to"
        className="relative overflow-hidden rounded-xl border border-transparent bg-gradient-to-r from-amber-50 via-white to-indigo-50 dark:from-amber-500/10 dark:via-slate-900/60 dark:to-indigo-500/10 px-4 ring-1 ring-amber-200/70 dark:ring-amber-400/20 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_28px_-18px_rgba(245,158,11,0.35)] animate-le-guide-glow"
      >
        {/* subtle sweeping shimmer behind the row — draws the eye without noise */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 -left-24 w-24 bg-gradient-to-r from-transparent via-white/70 to-transparent dark:via-white/10 blur-sm animate-le-guide-shimmer"
        />
        <AccordionTrigger
          className="relative hover:no-underline py-3 [&>svg]:text-amber-600 [&[data-state=open]>svg]:text-slate-500"
          data-testid="linkedin-export-guide-trigger"
        >
          <div className="flex flex-1 items-center gap-2.5 text-left min-w-0">
            <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500 text-white shrink-0 shadow-[0_4px_16px_-4px_rgba(245,158,11,0.55)]">
              <BookOpen size={15} />
              {/* pulsing ping dot to signal "look here" */}
              <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900" />
              </span>
            </span>
            <div className="min-w-0">
              <p className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                How to export your LinkedIn profile
                <span className="inline-flex items-center rounded-full bg-amber-500 text-white text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 shadow-sm">
                  Start here
                </span>
              </p>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                <Clock size={11} /> Takes a few seconds
                <span className="hidden sm:inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 font-medium">
                  · Tap to see the 5-sec walkthrough
                  <ArrowRight size={11} className="animate-le-guide-nudge" />
                </span>
              </p>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent
          className="relative pb-5 pt-1"
          data-testid="linkedin-export-guide-content"
        >
          {/* GIF / animation slot */}
          <div
            className="rounded-lg border border-border bg-secondary/40 overflow-hidden mb-4"
            data-testid="linkedin-export-guide-gif-slot"
          >
            {GIF_URL ? (
              <img
                src={GIF_URL}
                alt="Animated walkthrough: exporting a LinkedIn profile to PDF"
                className="w-full h-auto block"
              />
            ) : (
              <div className="flex items-center gap-3 px-4 py-6 text-muted-foreground">
                <Film size={18} className="text-primary shrink-0" />
                <div className="text-xs leading-relaxed">
                  <p className="font-medium text-foreground">
                    Animated walkthrough coming soon
                  </p>
                  <p className="mt-0.5">
                    A short looping GIF of the LinkedIn export flow will appear
                    here. The numbered steps below are accurate today.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Numbered steps */}
          <ol className="space-y-3" data-testid="linkedin-export-guide-steps">
            {STEPS.map((s) => (
              <li
                key={s.n}
                className="flex gap-3"
                data-testid={`linkedin-export-step-${s.n}`}
              >
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-[11px] font-mono-edge font-semibold">
                  {s.n}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{s.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    {s.detail}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-4 flex items-center gap-2">
            <Badge
              variant="secondary"
              className="font-mono-edge text-[10px] tracking-[0.16em]"
            >
              PRIVATE
            </Badge>
            <p className="text-xs text-muted-foreground">
              Your PDF never leaves your account. We extract structured fields
              and discard the file.
            </p>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export default LinkedInExportGuide;
